import { useMutation, useQuery } from '@apollo/client';
import {
  Mutation,
  MutationAssignToAccessGrantsArgs,
  MutationUnassignFromAccessGrantsArgs,
  Query
} from 'graphql/generated/graphqlTypes';
import { ASSIGN_TO_ACCESS_GRANTS, UNASSIGN_TO_ACCESS_GRANTS } from 'graphql/mutations';
import {
  GET_ALL_VISITORS_GROUPS,
  GET_VISITOR_DETAILS_DOORS,
  GET_VISITOR_DETAILS_LOCK_GROUPS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect } from 'react';

interface Props extends BaseHookProps {
  visitorGroupId: string;
}
const useDevicesTab = ({ handleFetchError, visitorGroupId }: Props) => {
  const { translation } = useTranslation();
  const {
    loading: locationsLoading,
    error: locationsError,
    data: locationsData
  } = useQuery<Pick<Query, 'getLocationsForTenant'>>(GET_VISITOR_DETAILS_LOCK_GROUPS, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (locationsError) {
      handleFetchError(translation.device_group_error_fetching);
      console.error(locationsError);
    }
  }, [locationsError]);

  const {
    loading: doorsLoading,
    error: doorsError,
    data: doorsData
  } = useQuery<Pick<Query, 'getDoorsForTenant'>>(GET_VISITOR_DETAILS_DOORS, {
    fetchPolicy: 'network-only'
  });

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  const [assignToAccessGrantsMutation, { loading: assignToAccessGrantsMutationLoading }] = useMutation<
    Pick<Mutation, 'assignToAccessGrants'>,
    MutationAssignToAccessGrantsArgs
  >(ASSIGN_TO_ACCESS_GRANTS, {
    onError: (error) => {
      handleFetchError(translation.access_grant_error_adding);
      console.error(error);
    },
    refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS }],
    awaitRefetchQueries: true
  });

  const [unassignFromAccessGrantsMutation, { loading: unassignFromAccessGrantsMutationLoading }] = useMutation<
    Pick<Mutation, 'unassignFromAccessGrants'>,
    MutationUnassignFromAccessGrantsArgs
  >(UNASSIGN_TO_ACCESS_GRANTS, {
    onError: (error) => {
      handleFetchError(translation.access_grant_error_removing);
      console.error(error);
    },
    refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS }],
    awaitRefetchQueries: true
  });

  const assignToAccessGrants = async (accessGrantIds: string[], doorIds: string[], locationIds: string[]) => {
    const { data } = await assignToAccessGrantsMutation({
      variables: {
        accessGrantIds,
        doorIds,
        locationIds
      }
    });
    return data;
  };

  const unassignFromAccessGrants = async (accessGrantIds: string[], doorIds: string[], locationIds: string[]) => {
    const { data } = await unassignFromAccessGrantsMutation({
      variables: {
        accessGrantIds,
        doorIds,
        locationIds
      }
    });
    return data;
  };

  return {
    deviceGroups: locationsData?.getLocationsForTenant || [],
    deviceGroupsLoading: locationsLoading,
    doors: doorsData?.getDoorsForTenant ?? [],
    doorsLoading: doorsLoading,
    assignToAccessGrants,
    assignToAccessGrantsMutationLoading,
    unassignFromAccessGrants,
    unassignFromAccessGrantsMutationLoading
  };
};

export default useDevicesTab;
